'use client';

import { useTranslations } from 'next-intl';
import { Loader2, type LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { GoalsEvaluation } from '@/types/project';

// Per-variant notice palette (surface / border / text), mirrored from the shared
// NoticeBanner so the model's brief feedback reads as the same family — but
// icon-less and with a caret pointing at the quality indicator. The variant is
// server-driven (goals_evaluation_variant): a workable brief reads as reassuring
// teal, only a weak one stays amber.
type FeedbackVariant = 'warning' | 'info' | 'success';

const VARIANT_TOKENS: Record<
  FeedbackVariant,
  { surface: string; border: string; text: string }
> = {
  warning: {
    surface: 'bg-[#FBFBE4] dark:bg-amber-900/20',
    border: 'border-[rgba(217,119,6,0.18)] dark:border-[rgba(180,83,9,0.45)]',
    text: 'text-[#594D33] dark:text-amber-400',
  },
  // info / success share the teal palette (matches NoticeBanner).
  info: {
    surface: 'bg-[#D6E6E6] dark:bg-teal-900/20',
    border: 'border-[rgba(7,141,134,0.18)] dark:border-[rgba(15,118,110,0.45)]',
    text: 'text-[#2D3A3A] dark:text-teal-400',
  },
  success: {
    surface: 'bg-[#D6E6E6] dark:bg-teal-900/20',
    border: 'border-[rgba(7,141,134,0.18)] dark:border-[rgba(15,118,110,0.45)]',
    text: 'text-[#2D3A3A] dark:text-teal-400',
  },
};

/**
 * Map the 1..3 quality score to the notice palette — the same client-side
 * score→presentation mapping the signal-bar indicator already does. A workable
 * brief reads as reassuring teal, only a weak one stays amber:
 *   1 → warning (amber), 2 → info (teal), 3 → success (teal).
 * Unknown/null (pending or never evaluated) falls back to warning.
 */
function variantForScore(score?: number | null): FeedbackVariant {
  if (score === 3) return 'success';
  if (score === 2) return 'info';
  return 'warning';
}

interface GoalsEvaluationFeedbackProps {
  evaluation?: GoalsEvaluation | null;
  pending?: boolean;
  /** 1..3 quality score — drives the card's severity palette (see
   *  variantForScore). */
  score?: number | null;
  /** Draw an upward caret on the card's top-right edge that points at the brief
   *  quality indicator above it (the Brief screen layout). */
  tail?: boolean;
}

/**
 * Server-side evaluation of the whole brief — text AND photos — shown verbatim
 * from the model. Rendered as a NoticeBanner-family card whose severity follows
 * the quality score (weak → amber warning, workable → teal info/success) but
 * with NO leading icon and an upward caret that points at the brief quality
 * indicator above it. Texts come verbatim from the API (already localized +
 * tone-shaped). Shared shape with Apple/Android.
 */
export function GoalsEvaluationFeedback({
  evaluation,
  pending,
  score,
  tail = false,
}: GoalsEvaluationFeedbackProps) {
  const t = useTranslations('project');
  // Pending has no settled score yet — keep the neutral warning tone until the
  // evaluation lands, then color by score.
  const tokens = VARIANT_TOKENS[pending ? 'warning' : variantForScore(score)];

  if (pending) {
    return (
      <ModelSpeechCard icon={Loader2} spin tail={tail} tokens={tokens}>
        <p className={cn('text-notice-body leading-[1.3]', tokens.text)}>
          {t('goalsEvaluating')}
        </p>
      </ModelSpeechCard>
    );
  }

  const suggestions = evaluation?.suggestions ?? [];
  if (suggestions.length === 0) return null;

  return (
    <ModelSpeechCard tail={tail} tokens={tokens}>
      {suggestions.map((suggestion, i) => (
        <p key={i} className={cn('text-notice-body leading-[1.3]', tokens.text)}>
          {suggestion}
        </p>
      ))}
    </ModelSpeechCard>
  );
}

/**
 * The model's brief feedback as a NoticeBanner-family card (score-driven
 * severity palette) — but icon-less, and with an upward caret on its
 * top-right edge that points at the quality indicator above. The caret apex
 * centers 24px from the right edge, matching the indicator's trailing inset so
 * it points vertically straight at it. Only the transient "evaluating" state
 * shows a spinner.
 */
function ModelSpeechCard({
  icon: Icon,
  spin = false,
  tail = false,
  tokens,
  children,
}: {
  icon?: LucideIcon;
  spin?: boolean;
  tail?: boolean;
  tokens: { surface: string; border: string; text: string };
  children: React.ReactNode;
}) {
  return (
    <div className={cn('relative rounded-2xl border p-4', tokens.surface, tokens.border)}>
      {/* Upward caret on the top-right edge, pointing at the brief quality
          indicator above. A rotated square with its two top edges bordered; its
          fill covers the card's top border underneath so the junction is seamless.
          right-[18px] → caret center sits 24px from the right edge (12px box,
          centered), aligned with the indicator's matching trailing inset. */}
      {tail && (
        <span
          aria-hidden
          className={cn(
            'absolute -top-1.5 right-[18px] h-3 w-3 rotate-45 rounded-tl-[2px] border-l border-t',
            tokens.surface,
            tokens.border
          )}
        />
      )}
      <div className="flex items-start gap-2.5">
        {Icon && (
          <Icon
            className={cn(
              'h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400',
              spin && 'animate-spin'
            )}
          />
        )}
        <div className="min-w-0 flex-1 space-y-2">{children}</div>
      </div>
    </div>
  );
}
